import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { matchPath, useLocation, useSearchParams } from 'react-router-dom'

import { UNAUTHENTICATED_REDIRECT_URL } from '@lyrahealth-inc/lyraweb-mobile/src/hooks/useShareSessionStorageAcrossTabs'
import { SESSION_STORAGE_KEY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  careRoutes,
  getRoutesEnabledInNativeApp,
  sessionsRoutes,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { useGetIsMobileWebBrowser } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useGetIsMobileWebBrowser'

import { getIsUserLoggedIn } from '../../data/user/userSelectors'

const usePathChanged = () => {
  const { pathname } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isLoggedIn = useSelector(getIsUserLoggedIn)
  const { is1LyraEnabledForHomebase, is1LyraEnabledForDirectBookingLinks, is1LyraEnabledForSessions } = useFlags()
  const isMobileBrowser = useGetIsMobileWebBrowser()
  const prevPathname = useRef('')
  const unauthenticatedRedirectUrl = searchParams.get('unauthenticatedRedirectUrl')

  // Fix scroll issues on mobile chrome. Contain this to the logged out site only, in case anything in app needs to maintain scroll position.
  useEffect(() => {
    if (isMobileBrowser && !isLoggedIn && prevPathname.current !== pathname) {
      scroll(0, 0)
    }
    prevPathname.current = pathname
  }, [isLoggedIn, isMobileBrowser, pathname])

  useEffect(() => {
    // If NATIVE_APP_FROM_LW is already set AND this is a reload, a previous handoff attempt
    // just failed (e.g., mobile dev server not running) — skip to prevent infinite reloads.
    // On fresh navigations (e.g., after login), allow the handoff even if the flag is stale.
    // ** Should ignore previous reloads (from other routes)
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    const loadedPathname = navigationEntry?.name ? new URL(navigationEntry.name).pathname : ''
    const reloadedOntoThisRoute = navigationEntry?.type === 'reload' && loadedPathname === pathname
    const handoffAlreadyFailed =
      reloadedOntoThisRoute && window.sessionStorage.getItem(SESSION_STORAGE_KEY.NATIVE_APP_FROM_LW) === 'true'

    const isCareRoute = careRoutes.some((route) => matchPath(route, pathname))
    const isSessionsRoute =
      is1LyraEnabledForSessions !== undefined &&
      is1LyraEnabledForSessions &&
      sessionsRoutes.some((route) => matchPath(route, pathname))
    const isOtherNativeRoute =
      is1LyraEnabledForHomebase !== undefined &&
      getRoutesEnabledInNativeApp(
        is1LyraEnabledForHomebase,
        is1LyraEnabledForDirectBookingLinks,
        is1LyraEnabledForSessions,
      ).some((route) => matchPath(route, pathname))

    if ((isCareRoute || isSessionsRoute || isOtherNativeRoute) && !handoffAlreadyFailed) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY.NATIVE_APP_FROM_LW, 'true')
      window.location.reload()
    }
  }, [pathname, is1LyraEnabledForHomebase, is1LyraEnabledForDirectBookingLinks, is1LyraEnabledForSessions])

  // Set unauthenticatedRedirectUrl in sessionStorage from query param
  // Used to persist the redirect url when changing subdomains
  useEffect(() => {
    if (unauthenticatedRedirectUrl) {
      window.sessionStorage.setItem(UNAUTHENTICATED_REDIRECT_URL, unauthenticatedRedirectUrl)

      // Remove the unauthenticatedRedirectUrl param from the URL without reloading
      setSearchParams((params) => {
        const newParams = new URLSearchParams(params)
        newParams.delete('unauthenticatedRedirectUrl')
        return newParams
      })
    }
  }, [unauthenticatedRedirectUrl, setSearchParams])
}

export default usePathChanged
